import { 
    AddressType, 
    CountryType, 
    CreateClaimFacility, 
    StateType,
    SupportFacility
} from '../../../../api';
import { getOptions } from '../../../base/select';
import { Field, Formik, FormikProps } from 'formik';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import * as S from './facility-form.styles';
import * as CS from '../section.styles';
import { InputField, SelectField } from '../../../base/form';
import { BaseButton } from '../../../base';
import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { validateFacilityFormValues } from '../../../../stores/claim-editor/claim-editor-rules';
import { fieldDomId } from '../../../../stores/claim-editor/claim-editor-sections';
import { useClaimEditorRevealFieldErrors } from '../use-claim-editor-form-validation';

interface FacilityFormValues {
    facilityLookup?: string;
    facilityId?: string;
    name: string;
    ein: string;
    npi: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: StateType;
    postalCode: string;
    country: CountryType;
}

const initialValues: FacilityFormValues = {
    facilityId: undefined,
    name: '',
    ein: '',
    npi: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: StateType.AL,
    postalCode: '',
    country: CountryType.US,
};

interface IFacilityFormProps {
    claimEditorStore: ClaimEditorStore;
}

export const FacilityForm: React.FunctionComponent<IFacilityFormProps> = observer(props => {
    const { claimEditorStore } = props;
    const validationSignal = claimEditorStore.validationSignal;
    const validationEvaluated = claimEditorStore.validationEvaluated;
    const formikRef = useRef<FormikProps<FacilityFormValues> | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(Boolean(claimEditorStore?.facility));
    const lookupDebounceRef = useRef<NodeJS.Timeout | null>(null);

    // Convert CreateClaimFacility to form values
    const getInitialValues = useCallback((): FacilityFormValues => {
        if (claimEditorStore?.facility) {
            const facility = claimEditorStore.facility;
            const address = facility.addresses?.[0];

            return {
                facilityLookup: facility.name || '',
                facilityId: facility.facilityId,
                name: facility.name || '',
                ein: facility.ein || '',
                npi: facility.npi || '',
                addressLine1: address?.lines?.[0] || '',
                addressLine2: address?.lines?.[1] || '',
                city: address?.city || '',
                state: address?.state || StateType.AL,
                postalCode: address?.postal || '',
                country: address?.country || CountryType.US,
            };
        }
        return { ...initialValues, facilityLookup: '' };
    }, [claimEditorStore?.facility]);

    // Convert form values to CreateClaimFacility
    const convertToFacility = (values: FacilityFormValues): CreateClaimFacility => {
        const lines = [values.addressLine1];
        if (values.addressLine2) {
            lines.push(values.addressLine2);
        }

        return {
            facilityId: values.facilityId || undefined,
            name: values.name,
            ein: values.ein || undefined,
            npi: values.npi || undefined,
            addresses: [
                {
                    name: values.name,
                    lines,
                    city: values.city,
                    state: values.state,
                    postal: values.postalCode,
                    country: values.country,
                    type: AddressType.MAILING,
                },
            ],
        };
    };

    // Debounced save to store
    const debouncedSave = useCallback(
        _.debounce(() => {
            const latestValues = formikRef.current?.values;
            if (!latestValues) return;
            const facilityData = convertToFacility(latestValues);
            claimEditorStore?.updateFacility(facilityData);
        }, 500),
        [claimEditorStore],
    );

    // Populate form when facility is selected from autocomplete
    const handleFacilitySelect = useCallback(
        (facility: SupportFacility, formik: FormikProps<FacilityFormValues>) => {
            const address = facility.addresses?.[0];

            const newValues: FacilityFormValues = {
                facilityLookup: facility.name || '',
                facilityId: facility.id || undefined,
                name: facility.name || '',
                ein: facility.ein || '',
                npi: facility.npi || '',
                addressLine1: address?.lines?.[0] || '',
                addressLine2: address?.lines?.[1] || '',
                city: address?.city || '',
                state: address?.state || StateType.AL,
                postalCode: address?.postal || '',
                country: address?.country || CountryType.US,
            };

            formik.setValues(newValues);
            debouncedSave();
        },
        [debouncedSave],
    );

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            debouncedSave.cancel();
            if (lookupDebounceRef.current) {
                clearTimeout(lookupDebounceRef.current);
            }
        };
    }, [debouncedSave]);

    const stateOptions = getOptions(StateType);
    const countryOptions = getOptions(CountryType);

    useEffect(() => {
        setIsEditing(Boolean(claimEditorStore?.facility));
    }, [claimEditorStore?.facility]);

    const doSearch = useCallback(
        async (q: string) => {
            const query = q.trim();
            if (!query) {
                return (claimEditorStore as any).searchFacilities(query, undefined);
            }

            // UUID-ish values are treated as IDs first, then we fallback to name search.
            const looksLikeFacilityId = /^[a-f0-9-]{8,}$/i.test(query);
            if (looksLikeFacilityId) {
                const idResults = await (claimEditorStore as any).searchFacilities(undefined, query);

                // Fallback to name search if no facilities were found by ID.
                if (Array.isArray(idResults) && idResults.length === 0) {
                    return (claimEditorStore as any).searchFacilities(query, undefined);
                }

                return idResults;
            }

            return (claimEditorStore as any).searchFacilities(query, undefined);
        },
        [claimEditorStore],
    );

    return (
        <>
        {!isEditing ? (
            <CS.ButtonContainer>
                <BaseButton variant="primary" onClick={(): void => setIsEditing(true)}>+ Add Facility</BaseButton>
            </CS.ButtonContainer>
        ) : null}

        {isEditing && (
        <Formik<FacilityFormValues>
            innerRef={formikRef as any}
            initialValues={getInitialValues()}
            validate={values =>
                validateFacilityFormValues(values, {
                    isFacilityIdActive: claimEditorStore.isFacilityIdActive,
                })
            }
            onSubmit={() => {}}
            enableReinitialize
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={false}
        >
            {formik => {
                // Auto-save on value change - save whenever values change, not just when dirty
                useEffect(() => {
                    debouncedSave();
                }, [formik.values, debouncedSave]);

                useClaimEditorRevealFieldErrors(formik, validationSignal, validationEvaluated);

                return (
                    <S.FormContainer>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <BaseButton variant="warning" onClick={(): void => { claimEditorStore.deleteFacility(); setIsEditing(false); }}>✕ Remove Facility</BaseButton>
                        </div>

                        <S.FormGrid>
                            <S.FormField span={2}>
                                <S.Label>Facility Lookup</S.Label>
                                <Field
                                    name="facilityLookup"
                                    component={AsyncSelectField}
                                    placeholder="Type facility name to search"
                                    loadOptions={async (q: string) => {
                                        // Empty query = show all facilities, non-empty = filter by name
                                        const results = await doSearch(q);
                                        return results.map((f: SupportFacility) => ({ 
                                            label: f.name || '', 
                                            value: f.id as any, 
                                            facility: f,
                                            info: (() => {
                                                const address = [
                                                    f.addresses?.[0]?.lines?.[0],
                                                    f.addresses?.[0]?.lines?.[1],
                                                    f.addresses?.[0]?.lines?.[2],
                                                    f.addresses?.[0]?.city,
                                                    f.addresses?.[0]?.state,
                                                    f.addresses?.[0]?.country,
                                                    f.addresses?.[0]?.postal
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ');
                                                return address ? ' | ' + address : undefined;
                                            })(),
                                        } as any));
                                    }}
                                    isClearable={true}
                                    onChange={(opt: any) => {
                                        if (!opt) {
                                            const defaultValues: FacilityFormValues = {
                                                ...initialValues,
                                                facilityLookup: '',
                                            };
                                            formik.setValues(defaultValues);
                                            
                                            debouncedSave();
                                            return;
                                        }
                                        
                                        const f: SupportFacility = opt?.facility;
                                        if (!f) {
                                            formik.setFieldValue('facilityId', undefined);
                                            return;
                                        }
                                        if (f.id) {
                                            claimEditorStore.markFacilityIdActive(f.id);
                                        }
                                        handleFacilitySelect(f, formik);
                                    }}
                                />
                            </S.FormField>

                            <S.FormField id={fieldDomId('facility.name')}>
                                <S.Label required>Name</S.Label>
                                <Field name="name" placeholder="Facility Name" component={InputField} />
                            </S.FormField>

                            <S.FormField id={fieldDomId('facility.addresses[0].lines[0]')}>
                                <S.Label required>Address Line 1</S.Label>
                                <Field name="addressLine1" placeholder="Address Line 1" component={InputField} />
                            </S.FormField>

                            <S.FormField>
                                <S.Label>Address Line 2</S.Label>
                                <Field name="addressLine2" placeholder="Address Line 2" component={InputField} />
                            </S.FormField>

                            <S.FormField id={fieldDomId('facility.ein')}>
                                <S.Label>EIN</S.Label>
                                <Field name="ein" placeholder="EIN" component={InputField} maxLength={9 as any} />
                            </S.FormField>

                            <S.FormField id={fieldDomId('facility.npi')}>
                                <S.Label>NPI</S.Label>
                                <Field name="npi" placeholder="NPI" component={InputField} maxLength={10 as any} />
                            </S.FormField>

                            <S.FormField id={fieldDomId('facility.addresses[0].state')}>
                                <S.Label required>State</S.Label>
                                <Field name="state" component={SelectField} options={stateOptions as any} placeholder="Select State" isClearable={false} />
                            </S.FormField>

                            <S.FormField id={fieldDomId('facility.addresses[0].city')}>
                                <S.Label required>City</S.Label>
                                <Field name="city" placeholder="City" component={InputField} />
                            </S.FormField>

                            <S.FormField id={fieldDomId('facility.addresses[0].country')}>
                                <S.Label required>Country</S.Label>
                                <Field name="country" component={SelectField} options={countryOptions as any} placeholder="Select Country" isClearable={false} />
                            </S.FormField>

                            <S.FormField id={fieldDomId('facility.addresses[0].postal')}>
                                <S.Label required>Postal Code</S.Label>
                                <Field name="postalCode" placeholder="Postal Code" component={InputField} />
                            </S.FormField>
                        </S.FormGrid>
                    </S.FormContainer>
                );
            }}
        </Formik>
        )}
        </>
    );
});

